import React, { useState, useEffect } from "react";
import { AlertCircle } from "lucide-react";
import { ReactComponent as Logo } from "../../assets/icons/svg/triva_logo.svg";
import { API_URLS } from "../api/urls";
import { AuthStorage } from "../desktop_screens/auth/AuthStorage";
import LoginForm from "../desktop_screens/auth/Login_Form";
import OTPVerification from "../desktop_screens/auth/OTP_Verification";
import UserRegistration from "../desktop_screens/auth/User_Registration";
import { PlanSelectionPayment } from "./Plan_Selection_Payment";
import PaymentSuccessPage from "./Payment_Success_Page_Mobile";

// ─── Types ────────────────────────────────────────────────────────────────────

interface PricingOption {
  duration: string;
  price: string;
  originalPrice?: string;
  standardDetailsId: number;
  productId: string;
}

interface SelectedPlanData {
  id: string;
  title: string;
  subtitle: string;
  description: string;
  pricingOptions: PricingOption[];
  whatYouGet: string[];
  whatsNotIncluded: string[];
}

interface LoginData {
  user_id: number;
  access_token: string;
  refresh_token: string;
  contactInfo: string;
  contactType: "phone" | "email";
  otp?: number;
  isGoogleAuthenticated?: boolean;
  [key: string]: any;
}

interface PaymentSuccessData {
  planType: "lite" | "spark" | "ignite" | "thrive" | "premium";
  duration: number;
  paymentDetails: {
    orderId: string;
    paymentId: string;
    amount: number;
    transactionDate: string;
    invoiceNumber?: string;
    invoicePdf?: string;
    userPaymentId?: number;
  };
}

type ViewType =
  | "loading"
  | "planNotFound"
  | "login"
  | "otpVerification"
  | "userRegistration"
  | "planDetails"
  | "paymentSuccess";

// ─── Helpers ──────────────────────────────────────────────────────────────────

const getPlanType = (name: string): "lite" | "spark" | "ignite" | "thrive" | "premium" => {
  const lower = name.toLowerCase();
  if (lower.includes("spark")) return "spark";
  if (lower.includes("ignite")) return "ignite";
  if (lower.includes("thrive")) return "thrive";
  if (lower.includes("signature")) return "premium";
  if (lower.includes("lite")) return "lite";
  return "lite";
};

// PlanSelectionPayment only accepts standard types
const toStandardPlanType = (
  type: string,
): "lite" | "spark" | "ignite" | "thrive" => {
  if (type === "spark" || type === "ignite" || type === "thrive") return type;
  return "lite";
};

const splitToArray = (value: string): string[] => {
  if (!value) return [];
  const cleaned = value.replace(/<br\s*\/?>/gi, "\n");
  if (cleaned.includes("\n")) {
    return cleaned.split(/\r?\n/).map((s) => s.trim()).filter(Boolean);
  }
  return cleaned.split(",").map((s) => s.trim()).filter(Boolean);
};

// ─── Component ────────────────────────────────────────────────────────────────

interface DirectPurchasePageMobileProps {
  standardDetailsId: number;
}

const DirectPurchasePageMobile: React.FC<DirectPurchasePageMobileProps> = ({
  standardDetailsId,
}) => {
  const [view, setView] = useState<ViewType>("loading");
  const [planData, setPlanData] = useState<SelectedPlanData | null>(null);
  const [planType, setPlanType] = useState<"lite" | "spark" | "ignite" | "thrive" | "premium">("lite");
  const [selectedDuration, setSelectedDuration] = useState<string | null>(null);
  const [loginData, setLoginData] = useState<LoginData | null>(null);
  const [verifiedUserId, setVerifiedUserId] = useState<number | null>(null);
  const [verifiedAccessToken, setVerifiedAccessToken] = useState<string>("");
  const [isGoogleProcessing, setIsGoogleProcessing] = useState(false);
  const [paymentSuccessData, setPaymentSuccessData] =
    useState<PaymentSuccessData | null>(null);

  // ── Fetch the plan matching standardDetailsId ────────────────────────────
  useEffect(() => {
    const findPlan = async () => {
      try {
        const res = await fetch(API_URLS.GET_COMPLEMENTARY_PLANS);
        if (!res.ok) throw new Error("fetch failed");

        const data = await res.json();
        if (data.success !== "1") throw new Error("api error");

        for (const apiPlan of data.plans as any[]) {
          const options: any[] = apiPlan.duration_and_price_and_perks || [];
          const match = options.find(
            (opt) =>
              Number(opt.standard_details_id) === Number(standardDetailsId)
          );

          if (match) {
            const type = getPlanType(apiPlan.subscription_plan_name);

            const pricingOptions: PricingOption[] = options.map((opt) => ({
              duration: opt.duration_period,
              price:
                opt.std_subscription_discount_price ||
                opt.std_subscription_price ||
                "0",
              originalPrice:
                opt.std_subscription_discount_price !== opt.std_subscription_price
                  ? opt.std_subscription_price
                  : undefined,
              standardDetailsId: Number(opt.standard_details_id),
              productId: opt.product_id || "",
            }));

            const plan: SelectedPlanData = {
              id: type,
              title: apiPlan.subscription_plan_name,
              subtitle: match.std_subscription_description || "",
              description: match.std_subscription_description || "",
              pricingOptions,
              whatYouGet: splitToArray(apiPlan.std_subscription_perks || ""),
              whatsNotIncluded: splitToArray(
                match.std_subscription_not_included || ""
              ),
            };

            setPlanData(plan);
            setPlanType(type);
            setSelectedDuration(match.duration_period);
            setView(AuthStorage.isAuthenticated() ? "planDetails" : "login");
            return;
          }
        }
        setView("planNotFound");
      } catch (err) {
        setView("planNotFound");
      }
    };

    findPlan();
  }, [standardDetailsId]);

  // ── Auth handlers ────────────────────────────────────────────────────────

  const handleOTPRequired = (data: LoginData) => {
    AuthStorage.setAuthData(
      data.access_token,
      data.refresh_token,
      data.user_id
    );
    setLoginData(data);
    setIsGoogleProcessing(false);
    setView("otpVerification");
  };

  const handleOTPSuccess = (userData: any) => {
    const token = loginData?.access_token || "";
    const userId = userData.user_id;

    setVerifiedUserId(userId);
    setVerifiedAccessToken(token);

    if (loginData) {
      AuthStorage.setAuthData(loginData.access_token, loginData.refresh_token, userId, userData);
    } else {
      AuthStorage.setUserData(userData);
    }
    setLoginData(null);
    if (!userData.user_fname || userData.user_fname.trim() === "") {
      setView("userRegistration");
    } else {
      setView("planDetails");
    }
  };

  const handleGoogleLoginSuccess = (
    _userData: any,
    needsRegistration: boolean
  ) => {
    setIsGoogleProcessing(false);
    setView(needsRegistration ? "userRegistration" : "planDetails");
  };

  const handleRegistrationSuccess = (firstName: string, lastName: string) => {
    const existing = AuthStorage.getUserData();
    if (existing) {
      AuthStorage.setUserData({
        ...existing,
        user_fname: firstName,
        user_lname: lastName,
      });
    }
    setView("planDetails");
  };

  const handlePaymentSuccess = (
    _pType: "lite" | "spark" | "ignite" | "thrive",
    duration: number,
    paymentDetails: any
  ) => {
    // Use the planType state directly — _pType is degraded by toStandardPlanType
    // (e.g. "premium" becomes "lite") so it cannot be trusted here.
    setPaymentSuccessData({ planType, duration, paymentDetails });
    setView("paymentSuccess");
  };

  const goHome = () => {
    window.location.href = "https://www.mytriva.com";
  };

  // ── Payment success view (full screen) ──────────────────────────────────

  if (view === "paymentSuccess" && paymentSuccessData) {
    return (
      <PaymentSuccessPage
        userId={AuthStorage.getUserId()}
        planType={paymentSuccessData.planType}
        selectedDuration={paymentSuccessData.duration}
        paymentDetails={paymentSuccessData.paymentDetails}
        onNavigateHome={goHome}
        preloadedFeatures={planData?.whatYouGet}
      />
    );
  }

  // ── Shared shell ─────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Header — hidden during auth screens since LoginForm renders its own logo */}
      {(view === "loading" || view === "planNotFound" || view === "planDetails") && (
        <div className="w-full px-6 py-4">
          <div
            className="w-[100px] h-[30px] cursor-pointer"
            onClick={goHome}
          >
            <Logo className="w-full h-full object-contain" />
          </div>
        </div>
      )}

      {/* Content */}
      <div className="flex-1">

        {/* Loading */}
        {view === "loading" && (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#0DB5B5]" />
          </div>
        )}

        {/* Plan not found */}
        {view === "planNotFound" && (
          <div className="text-center px-6 py-16">
            <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-[#202020] mb-2">
              Plan Not Found
            </h2>
            <p className="text-[#646464] mb-6">
              This plan link is invalid or has expired.
            </p>
            <button
              onClick={goHome}
              className="bg-[#0DB5B5] text-white px-6 py-2 rounded-lg font-medium"
            >
              View All Plans
            </button>
          </div>
        )}

        {/* Login */}
        {view === "login" && (
          <div className="px-[24px] mt-[32px]">
            <LoginForm
              onBack={goHome}
              showBackButton={false}
              onLoginSuccess={() => {}}
              onOTPRequired={handleOTPRequired}
              onGoogleLoginSuccess={handleGoogleLoginSuccess}
              isGoogleProcessing={isGoogleProcessing}
              onGoogleProcessingStart={() => setIsGoogleProcessing(true)}
            />
          </div>
        )}

        {/* OTP Verification */}
        {view === "otpVerification" && (
          <div className="px-[24px] mt-[32px]">
            <OTPVerification
              contactInfo={loginData?.contactInfo}
              contactType={loginData?.contactType}
              userId={loginData?.user_id}
              expectedOTP={loginData?.otp}
              onBack={() => setView("login")}
              onVerificationSuccess={handleOTPSuccess}
            />
          </div>
        )}

        {/* User Registration */}
        {view === "userRegistration" && (
          <div className="px-[24px] mt-[32px]">
            <UserRegistration
              onSubmit={handleRegistrationSuccess}
              onBack={() => setView("login")}
              initialFirstName={AuthStorage.getUserData()?.user_fname || ""}
              userId={verifiedUserId ?? AuthStorage.getUserId()}
              accessToken={verifiedAccessToken || AuthStorage.getAccessToken()}
            />
          </div>
        )}

        {/* Plan Details */}
        {view === "planDetails" && planData && (
          <PlanSelectionPayment
            planType={toStandardPlanType(planType)}
            userId={AuthStorage.getUserId()}
            authToken={null}
            planData={{
              title: planData.title,
              pricingOptions: planData.pricingOptions,
              whatYouGet: planData.whatYouGet,
              whatsNotIncluded: planData.whatsNotIncluded,
            }}
            selectedDuration={selectedDuration || planData.pricingOptions[0]?.duration || ""}
            onBack={goHome}
            onPaymentSuccess={handlePaymentSuccess}
            isComplimentary={true}
          />
        )}
      </div>

      {/* Footer */}
      {view !== "planDetails" && (
        <footer className="w-full py-4 px-6">
          <p className="text-center text-[11px] text-[#0000008C]">
            © 2024 - 2025 FitLife Fitness Foods LLP. All rights reserved.
          </p>
        </footer>
      )}
    </div>
  );
};

export default DirectPurchasePageMobile;
